import { useEffect, useRef, useState, cloneElement, isValidElement, Suspense } from 'react'
import PropTypes from 'prop-types'
import { Box, Drawer as MuiDrawer } from '@mui/material'
import { Cross } from '../../../assets/icons'
import IconButton from '../IconButton'

const heightBottomBar = 100

const minSize = { left: 320, right: 320, bottom: 220 }
const maxSize = anchor =>
  anchor === 'bottom' ? Math.round(window.innerHeight * 0.8) : Math.round(window.innerWidth * 0.9)

const Drawer = ({
  toggleButton = <button type="button">open drawer</button>,
  children,
  drawerWidth = 800,
  drawerHeight = 400,
  withBottomBar = false,
  bottomBarComponent = null,
  open: propOpen = false,
  setOpen: setPropOpen = () => {},
  variant = undefined,
  anchor = 'right',
  resizable = false,
  onResize = undefined,
  contentPadding = 6,
  bottomBarPadding = 6,
  closeButtonSx = undefined
}) => {
  const [open, setOpen] = useState(propOpen)
  useEffect(() => {
    setOpen(propOpen)
  }, [setOpen, propOpen])

  // Während des Ziehens hält dragSize die Live-Größe; nach dem Loslassen
  // übernimmt wieder die Prop (der Consumer persistiert via onResize).
  const [dragSize, setDragSize] = useState(null)
  const dragSizeRef = useRef(null)

  const isBottom = anchor === 'bottom'
  const effectiveWidth = !isBottom && dragSize !== null ? dragSize : drawerWidth
  const effectiveHeight = isBottom && dragSize !== null ? dragSize : drawerHeight

  const startResize = event => {
    event.preventDefault()
    const handle = event.currentTarget
    handle.setPointerCapture(event.pointerId)

    const onPointerMove = e => {
      let size
      if (anchor === 'right') size = window.innerWidth - e.clientX
      else if (anchor === 'left') size = e.clientX
      else size = window.innerHeight - e.clientY
      size = Math.round(Math.min(Math.max(size, minSize[anchor]), maxSize(anchor)))
      dragSizeRef.current = size
      setDragSize(size)
    }
    const onPointerUp = () => {
      handle.removeEventListener('pointermove', onPointerMove)
      handle.removeEventListener('pointerup', onPointerUp)
      if (dragSizeRef.current !== null) onResize?.(dragSizeRef.current)
      dragSizeRef.current = null
      setDragSize(null)
    }
    handle.addEventListener('pointermove', onPointerMove)
    handle.addEventListener('pointerup', onPointerUp)
  }

  const onClick = () => {
    setPropOpen?.(!open)
    setOpen(!open)
  }

  const onClose = () => {
    setOpen(false)
    setPropOpen?.(false)
  }

  return (
    <Suspense fallback={null}>
      {isValidElement(toggleButton) &&
        (open !== undefined ? cloneElement(toggleButton) : cloneElement(toggleButton, { onClick }))}
      <MuiDrawer variant={variant} anchor={anchor} open={open} onClose={onClose}>
        {resizable && (
          <Box
            onPointerDown={startResize}
            sx={{
              position: 'absolute',
              zIndex: 1,
              touchAction: 'none',
              userSelect: 'none',
              '&:hover': { backgroundColor: 'action.hover' },
              ...(isBottom
                ? { top: 0, left: 0, width: '100%', height: '6px', cursor: 'row-resize' }
                : {
                    top: 0,
                    [anchor === 'right' ? 'left' : 'right']: 0,
                    width: '6px',
                    height: '100%',
                    cursor: 'col-resize'
                  })
            }}
          />
        )}
        {/* closeButtonSx erlaubt Consumern mit farbigem Header (z.B. AiChat),
            Position und Farben des Schließen-Buttons anzupassen — Selektoren
            wie '& .MuiIconButton-root' greifen auf den Button durch. */}
        <Box sx={{ position: 'absolute', right: '28px', top: '28px', ...closeButtonSx }}>
          <IconButton color="secondary" icon={<Cross />} onClick={onClose} size="large" />
        </Box>
        <Box
          sx={{
            p: contentPadding,
            height: isBottom
              ? `${effectiveHeight - (withBottomBar ? heightBottomBar : 0)}px`
              : withBottomBar
                ? `calc(100% - ${heightBottomBar}px)`
                : '100%',
            overflowY: 'auto',
            // overflowX: 'hidden',
            width: isBottom ? '100vw' : effectiveWidth,
            maxWidth: '100vw'
          }}
        >
          {children}
        </Box>
        {withBottomBar && bottomBarComponent && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              backgroundColor: 'common.white',
              bottom: 0,
              // Zahl statt String: `${drawerWidth}` wäre einheitenloses CSS
              // (ungültig, wird ignoriert) — die Bar würde dann mit ihrem
              // Inhalt wachsen und den ganzen Drawer aufziehen.
              width: isBottom ? '100vw' : effectiveWidth,
              maxWidth: '100vw',
              height: `${heightBottomBar}px`,
              px: bottomBarPadding,
              boxShadow: '0 -2px 16px 0 rgba(75, 85, 99, 0.13)'
            }}
          >
            {bottomBarComponent}
          </Box>
        )}
      </MuiDrawer>
    </Suspense>
  )
}

Drawer.propTypes = {
  toggleButton: PropTypes.node,
  children: PropTypes.node.isRequired,
  drawerWidth: PropTypes.number,
  drawerHeight: PropTypes.number,
  withBottomBar: PropTypes.bool,
  open: PropTypes.bool,
  bottomBarComponent: PropTypes.node,
  setOpen: PropTypes.func,
  variant: PropTypes.oneOf(['temporary', 'permanent', 'persistent']),
  anchor: PropTypes.oneOf(['left', 'right', 'bottom']),
  resizable: PropTypes.bool,
  onResize: PropTypes.func,
  contentPadding: PropTypes.number,
  bottomBarPadding: PropTypes.number,
  closeButtonSx: PropTypes.object
}
export default Drawer
